import React, { useState, useEffect, useCallback } from 'react';
import API_BASE_URL from '../../config';
import { useNavigate } from 'react-router-dom';
import Sidebar from './SideBar';
import Header from '../Dashboard/Header.js';
import Lottie from 'lottie-react';
import loader from '../css/loader.json';
import { Box } from '@mui/material';

function Outstanding() {
  const navigate = useNavigate();

  const [type, setType] = useState('Receivable');
  const [dueDays, setDueDays] = useState('0');
  const [criteria, setCriteria] = useState('datewise');
  const [operator, setOperator] = useState('');
  const [date, setDate] = useState('');
  const [partyWiseValue, setPartyWiseValue] = useState('');
  const [salesmanName, setSalesmanName] = useState('');
  const [areaWiseValue, setAreaWiseValue] = useState('');
  const [rootWiseValue, setRootWiseValue] = useState('');
  const [combinedRootValue, setCombinedRootValue] = useState('');
  const [combinedSalesmanValue, setCombinedSalesmanValue] = useState('');
  const [subAgentValue, setSubAgentValue] = useState('');

  const [error, setError] = useState(null);
  const [partySuggestions, setPartySuggestions] = useState([]);
  const [salesmanSuggestions, setSalesmanSuggestions] = useState([]);
  const [areaSuggestions, setAreaSuggestions] = useState([]);
  const [rootSuggestions, setRootSuggestions] = useState([]);
  const [combinedRootSuggestions, setCombinedRootSuggestions] = useState([]);
  const [combinedSalesmanSuggestions, setCombinedSalesmanSuggestions] = useState([]);
  const [subAgentSuggestions, setSubAgentSuggestions] = useState([]);

  const [loading, setLoading] = useState(false);
  const [skip, setSkip] = useState([]);

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    setDate(today);
  }, []);


  const handleChange = (setter) => (event) => {
    setSkip([]);
    setter(event.target.value);
  };

  const fetchSubAgentSuggestions = useCallback(async () => {
    const userId = localStorage.getItem('userId');
    const accessToken = localStorage.getItem('accessToken');

    if (!subAgentValue.trim()) {
      setSubAgentSuggestions([]);
      return;
    }

    const selectedCompany = JSON.parse(localStorage.getItem('selectedCompany'));
    if (!selectedCompany || !selectedCompany.CompCode) {
      setError('Company code is missing or invalid');
      return;
    }

    const queryParams = new URLSearchParams({
      name: subAgentValue.trim().toLowerCase(),
      CompCode: selectedCompany.CompCode,
    });

    try {
      const response = await fetch(`${API_BASE_URL}/api/salesman-suggestions?${queryParams.toString()}`, {
        headers: {
          'UserId': userId,
          'Authorization': `Bearer ${accessToken}`,
        },
      });
      if (!response.ok) throw new Error('Network response was not ok');
      const data = await response.json();
      setSubAgentSuggestions(data);
    } catch (error) {
      console.error('Error fetching sub-agent suggestions:', error);
      setError(error.message);
    }
  }, [subAgentValue]);

  const fetchPartySuggestions = useCallback(async () => {
    const userId = localStorage.getItem('userId');
    const accessToken = localStorage.getItem('accessToken');

    if (!partyWiseValue) {
      setPartySuggestions([]);
      return;
    }

    const selectedCompany = JSON.parse(localStorage.getItem('selectedCompany'));
    if (!selectedCompany || !selectedCompany.CompCode) {
      setError('Company code is missing or invalid');
      return;
    }

    const queryParams = new URLSearchParams({
      name: partyWiseValue,
      CompCode: selectedCompany.CompCode,
    });

    try {
      const response = await fetch(`${API_BASE_URL}/api/getPartySuggestions?${queryParams.toString()}`, {
        headers: {
          'UserId': userId,
          'Authorization': `Bearer ${accessToken}`,
        },
      });
      if (!response.ok) throw new Error('Network response was not ok');
      const data = await response.json();
      setPartySuggestions(data);
    } catch (error) {
      console.error('Error fetching party suggestions:', error);
      setError(error.message);
    }
  }, [partyWiseValue]);

  const fetchSalesmanSuggestions = useCallback(async () => {
    const userId = localStorage.getItem('userId');
    const accessToken = localStorage.getItem('accessToken');

    if (!salesmanName.trim()) {
      setSalesmanSuggestions([]);
      return;
    }

    const selectedCompany = JSON.parse(localStorage.getItem('selectedCompany'));
    if (!selectedCompany || !selectedCompany.CompCode) {
      setError('Company code is missing or invalid');
      return;
    }

    const queryParams = new URLSearchParams({
      name: salesmanName.trim().toLowerCase(),
      CompCode: selectedCompany.CompCode,
    });

    try {
      const response = await fetch(`${API_BASE_URL}/api/salesman-suggestions?${queryParams.toString()}`, {
        headers: {
          'UserId': userId,
          'Authorization': `Bearer ${accessToken}`,
        },
      });
      if (!response.ok) throw new Error('Network response was not ok');
      const data = await response.json();
      setSalesmanSuggestions(data);
    } catch (error) {
      console.error('Error fetching salesman suggestions:', error);
      setError(error.message);
    }
  }, [salesmanName]);

  const fetchAreaSuggestions = useCallback(async () => {
    const userId = localStorage.getItem('userId');
    const accessToken = localStorage.getItem('accessToken');

    if (!areaWiseValue || !areaWiseValue.trim()) {
      setAreaSuggestions([]);
      return;
    }

    const selectedCompany = JSON.parse(localStorage.getItem('selectedCompany'));
    if (!selectedCompany || !selectedCompany.CompCode) {
      setError('Company code is missing or invalid');
      return;
    }

    const queryParams = new URLSearchParams({
      area: areaWiseValue.trim(),
      CompCode: selectedCompany.CompCode,
    });

    try {
      const response = await fetch(`${API_BASE_URL}/api/area-suggestions?${queryParams.toString()}`, {
        headers: {
          'UserId': userId,
          'Authorization': `Bearer ${accessToken}`,
        },
      });
      if (!response.ok) throw new Error('Network response was not ok');
      const data = await response.json();
      setAreaSuggestions(data);
    } catch (error) {
      console.error('Error fetching area suggestions:', error);
      setError(error.message);
    }
  }, [areaWiseValue]);

  const fetchRootSuggestions = useCallback(async () => {
    const userId = localStorage.getItem('userId');
    const accessToken = localStorage.getItem('accessToken');

    if (!rootWiseValue.trim()) {
      setRootSuggestions([]);
      return;
    }

    const selectedCompany = JSON.parse(localStorage.getItem('selectedCompany'));
    if (!selectedCompany || !selectedCompany.CompCode) {
      setError('Company code is missing or invalid');
      return;
    }

    const queryParams = new URLSearchParams({
      root: rootWiseValue.trim(),
      CompCode: selectedCompany.CompCode,
    });

    try {
      const response = await fetch(`${API_BASE_URL}/api/root-suggestions?${queryParams.toString()}`, {
        headers: {
          'UserId': userId,
          'Authorization': `Bearer ${accessToken}`,
        },
      });
      if (!response.ok) throw new Error('Network response was not ok');
      const data = await response.json();
      setRootSuggestions(data);
    } catch (error) {
      console.error('Error fetching root suggestions:', error);
      setError(error.message);
    }
  }, [rootWiseValue]);

  const fetchCombinedRootSuggestions = useCallback(async () => {
    const userId = localStorage.getItem('userId');
    const accessToken = localStorage.getItem('accessToken');

    if (!combinedRootValue.trim()) {
      setCombinedRootSuggestions([]);
      return;
    }

    const selectedCompany = JSON.parse(localStorage.getItem('selectedCompany'));
    if (!selectedCompany || !selectedCompany.CompCode) {
      setError('Company code is missing or invalid');
      return;
    }

    const queryParams = new URLSearchParams({
      root: combinedRootValue.trim(),
      CompCode: selectedCompany.CompCode,
    });

    try {
      const response = await fetch(`${API_BASE_URL}/api/root-suggestions?${queryParams.toString()}`, {
        headers: {
          'UserId': userId,
          'Authorization': `Bearer ${accessToken}`,
        },
      });
      if (!response.ok) throw new Error('Network response was not ok');
      const data = await response.json();
      setCombinedRootSuggestions(data);
    } catch (error) {
      console.error('Error fetching combined root suggestions:', error);
      setError(error.message);
    }
  }, [combinedRootValue]);

  const fetchCombinedSalesmanSuggestions = useCallback(async () => {
    const userId = localStorage.getItem('userId');
    const accessToken = localStorage.getItem('accessToken');

    if (!combinedSalesmanValue.trim()) {
      setCombinedSalesmanSuggestions([]);
      return;
    }

    const selectedCompany = JSON.parse(localStorage.getItem('selectedCompany'));
    if (!selectedCompany || !selectedCompany.CompCode) {
      setError('Company code is missing or invalid');
      return;
    }

    const queryParams = new URLSearchParams({
      name: combinedSalesmanValue.trim().toLowerCase(),
      CompCode: selectedCompany.CompCode,
    });

    try {
      const response = await fetch(`${API_BASE_URL}/api/salesman-suggestions?${queryParams.toString()}`, {
        headers: {
          'UserId': userId,
          'Authorization': `Bearer ${accessToken}`,
        },
      });
      if (!response.ok) throw new Error('Network response was not ok');
      const data = await response.json();
      setCombinedSalesmanSuggestions(data);
    } catch (error) {
      console.error('Error fetching combined salesman suggestions:', error);
      setError(error.message);
    }
  }, [combinedSalesmanValue]);

  useEffect(() => {
    if (skip.includes("combinerootname")) {
      return;
    }
    fetchCombinedRootSuggestions();
  }, [combinedRootValue, fetchCombinedRootSuggestions, skip]);

  useEffect(() => {
    if (skip.includes("combinesalesman")) {
      return;
    }
    fetchCombinedSalesmanSuggestions();
  }, [combinedSalesmanValue, fetchCombinedSalesmanSuggestions, skip]);

  useEffect(() => {
    if (skip.includes("partySuggestions")) {
      return;
    }
    fetchPartySuggestions();
  }, [partyWiseValue, fetchPartySuggestions, skip]);

  useEffect(() => {
    if (skip.includes("salesmanSuggestions")) {
      return;
    }
    fetchSalesmanSuggestions();
  }, [salesmanName, fetchSalesmanSuggestions, skip]);

  useEffect(() => {
    if (skip.includes("areaNameSuggestion")) {
      return;
    }
    fetchAreaSuggestions();
  }, [areaWiseValue, fetchAreaSuggestions, skip]);

  useEffect(() => {
    if (skip.includes("RootNameSuggestion")) {
      return;
    }
    fetchRootSuggestions();
  }, [rootWiseValue, fetchRootSuggestions, skip]);

  useEffect(() => {
    if (skip.includes("subagentSuggestion")) {
      return;
    }
    fetchSubAgentSuggestions();
  }, [subAgentValue, fetchSubAgentSuggestions, skip]);

  const handleSubmit = (event) => {
    event.preventDefault();

    const selectedCompany = JSON.parse(localStorage.getItem('selectedCompany'));
    if (!selectedCompany || !selectedCompany.CompCode) {
      setError('Company code is missing or invalid');
      return;
    }

    const selectedSalesmanCode = localStorage.getItem('selectedSalesmanCode');
    const selectedPartyCode = localStorage.getItem('selectedPartyCode');
    const selectedAreaCode = localStorage.getItem('selectedAreaCode');
    const selectedRootCode = localStorage.getItem('selectedRootCode');

    const queryParams = new URLSearchParams({
      type: type || '',
      dueDays: dueDays || '0',
      criteria: criteria || '',
      operator: operator || '',
      date: date || '',
      CompCode: selectedCompany.CompCode,
    });

    if (criteria === 'partywise') {
      queryParams.append('partyCode', selectedPartyCode || '');
    } else if (criteria === 'salesman') {
      queryParams.append('salesmanCode', selectedSalesmanCode || '');
    } else if (criteria === 'areawise') {
      queryParams.append('areaCode', selectedAreaCode || '');
    } else if (criteria === 'rootwise') {
      queryParams.append('rootCode', selectedRootCode || '');
    } else if (criteria === 'root-salesman') {
      queryParams.append('rootCode', localStorage.getItem('selectedRootCode') || '');
      queryParams.append('salesmanCode', localStorage.getItem('selectedSalesmanCode') || '');
    } else if (criteria === 'subagentwise') {
      queryParams.append('subAgentCode', localStorage.getItem('selectedSubAgentCode') || '');
    }

    navigate(`/dataTable?${queryParams.toString()}`);
  };

  const handleSuggestionClick = (value, setter, skipKey) => {
    setter(value);
    setSkip(prev => [...prev, skipKey]);
  };



  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(window.innerWidth <= 768);

  const handleSidebarToggle = useCallback((collapsed) => {
    setIsSidebarCollapsed(collapsed);
  }, []);

  return (
    <div className="flex h-screen overflow-hidden bg-gray-100 font-sans">
      <Sidebar onToggle={handleSidebarToggle} />
      <Box
        component="main"
        className={`flex-1 flex flex-col h-full transition-all duration-300 ${isSidebarCollapsed ? 'ml-0' : 'ml-0 md:ml-[260px]'}`}
      >
        <Header />
        <div className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50 p-4 md:p-6">
          <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-6 border-b border-gray-100 bg-gradient-to-r ">
              <h1 className="text-2xl font-bold text-center">Outstanding Report</h1>
              <p className="text-center mt-1 text-sm">Generate detailed outstanding reports based on various criteria</p>
            </div>

            <div className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">

                {/* Type Selection */}
                <div className="flex justify-center mb-8">
                  <div className="bg-gray-100 p-1 rounded-lg inline-flex border border-gray-200">
                    <label className={`cursor-pointer px-6 py-2 rounded-md transition-all duration-200 text-sm font-medium ${type === 'Receivable' ? 'bg-white text-blue-700 shadow-sm ring-1 ring-black/5' : 'text-gray-500 hover:text-gray-900'}`}>
                      <input
                        type="radio"
                        name="type"
                        value="Receivable"
                        checked={type === 'Receivable'}
                        onChange={handleChange(setType)}
                        className="hidden"
                      />
                      Receivable
                    </label>
                    <label className={`cursor-pointer px-6 py-2 rounded-md transition-all duration-200 text-sm font-medium ${type === 'Payables' ? 'bg-white text-blue-700 shadow-sm ring-1 ring-black/5' : 'text-gray-500 hover:text-gray-900'}`}>
                      <input
                        type="radio"
                        name="type"
                        value="Payables"
                        checked={type === 'Payables'}
                        onChange={handleChange(setType)}
                        className="hidden"
                      />
                      Payables
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Date Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Select Date</label>
                    <input
                      type="date"
                      value={date}
                      onChange={handleChange(setDate)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none transition-colors text-gray-700"
                    />
                  </div>

                  {/* Criteria Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Select Criteria</label>
                    <select
                      value={criteria}
                      onChange={handleChange(setCriteria)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none transition-colors bg-white text-gray-700"
                    >
                      <option value="datewise">DateWise</option>
                      <option value="partywise">PartyWise</option>
                      <option value="salesman">Salesman</option>
                      <option value="subagentwise">SubAgentWise</option>
                      <option value="rootwise">RootWise</option>
                      <option value="areawise">AreaWise</option>
                      <option value="root-salesman">Root + SalesmanWise</option>
                    </select>
                  </div>

                  {/* Operator Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Select Operator</label>
                    <select
                      value={operator}
                      onChange={handleChange(setOperator)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none transition-colors bg-white text-gray-700"
                    >
                      <option value="">-- Select an operator --</option>
                      <option value="=">=</option>
                      <option value="<">&lt;</option>
                      <option value=">">&gt;</option>
                      <option value="<=">&le;</option>
                      <option value=">=">&ge;</option>
                      <option value="<>">&ne;</option>
                    </select>
                  </div>

                  {/* Due Days */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Due Days</label>
                    <input
                      type="number"
                      value={dueDays}
                      onChange={handleChange(setDueDays)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none transition-colors text-gray-700"
                    />
                  </div>
                </div>

                {/* Dynamic Fields based on Criteria */}
                <div className="mt-6">
                  {criteria === 'partywise' && (
                    <div className="relative">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Party Name</label>
                      <input
                        type="text"
                        value={partyWiseValue}
                        onChange={handleChange(setPartyWiseValue)}
                        placeholder="Enter Party Name"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none transition-colors text-gray-700"
                      />
                      {partySuggestions.length > 0 && (
                        <ul className="absolute z-10 w-full bg-white mt-1 border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {partySuggestions.map((suggestion) => (
                            <li
                              key={`party-${suggestion.code}`}
                              onClick={() => {
                                setPartyWiseValue(suggestion.partyName);
                                setSkip(prev => [...prev, "partySuggestions"])
                                localStorage.setItem('selectedPartyCode', suggestion.code);
                                setPartySuggestions([]);
                              }}
                              className="px-4 py-2 hover:bg-blue-50 cursor-pointer text-gray-700 transition-colors border-b border-gray-100 last:border-0"
                            >
                              {suggestion.partyName}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}

                  {criteria === 'salesman' && (
                    <div className="relative">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Salesman Name</label>
                      <input
                        type="text"
                        value={salesmanName}
                        onChange={handleChange(setSalesmanName)}
                        placeholder="Enter Salesman Name"
                        autoComplete="off"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none transition-colors text-gray-700"
                      />
                      {salesmanSuggestions.length > 0 && (
                        <ul className="absolute z-10 w-full bg-white mt-1 border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {salesmanSuggestions.map((item) => (
                            <li
                              key={item.Code || item.code || item.salesmanCode || item.salesmancode || item.SalesmanCode || item.Salesmancode}
                              onClick={() => {
                                handleSuggestionClick(
                                  item.Name || item.salesmanName || item.name || item.salesmanname,
                                  setSalesmanName,
                                  "salesmanSuggestions"
                                );
                                setSalesmanSuggestions([]);
                              }}
                              className="px-4 py-2 hover:bg-blue-50 cursor-pointer text-gray-700 transition-colors border-b border-gray-100 last:border-0"
                            >
                              {item.Name || item.salesmanName || item.name || item.salesmanname}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}

                  {criteria === 'areawise' && (
                    <div className="relative">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Area Name</label>
                      <input
                        type="text"
                        value={areaWiseValue}
                        onChange={handleChange(setAreaWiseValue)}
                        placeholder="Enter Area Name"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none transition-colors text-gray-700"
                      />
                      {areaSuggestions.length > 0 && (
                        <ul className="absolute z-10 w-full bg-white mt-1 border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {areaSuggestions.map((suggestion, index) => (
                            <li
                              key={`area-${suggestion.serialno || index}`}
                              onClick={() => {
                                setAreaWiseValue(suggestion.Area);
                                localStorage.setItem('selectedAreaCode', suggestion.SerialNo);
                                setSkip(prev => [...prev, "areaNameSuggestion"])
                                setAreaSuggestions([]);
                              }}
                              className="px-4 py-2 hover:bg-blue-50 cursor-pointer text-gray-700 transition-colors border-b border-gray-100 last:border-0"
                            >
                              {suggestion.Area}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}

                  {criteria === 'rootwise' && (
                    <div className="relative">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Root Name</label>
                      <input
                        type="text"
                        value={rootWiseValue}
                        onChange={handleChange(setRootWiseValue)}
                        placeholder="Enter Root Name"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none transition-colors text-gray-700"
                      />
                      {rootSuggestions.length > 0 && (
                        <ul className="absolute z-10 w-full bg-white mt-1 border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {rootSuggestions.map((suggestion, index) => (
                            <li
                              key={`root-${suggestion.srno || index}`}
                              onClick={() => {
                                setRootWiseValue(suggestion.Root);
                                localStorage.setItem('selectedRootCode', suggestion.SrNo);
                                setSkip(prev => [...prev, "RootNameSuggestion"])
                                setRootSuggestions([]);
                              }}
                              className="px-4 py-2 hover:bg-blue-50 cursor-pointer text-gray-700 transition-colors border-b border-gray-100 last:border-0"
                            >
                              {suggestion.Root}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}

                  {criteria === 'root-salesman' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="relative">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Root Name</label>
                        <input
                          type="text"
                          value={combinedRootValue}
                          onChange={handleChange(setCombinedRootValue)}
                          placeholder="Enter Root Name"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none transition-colors text-gray-700"
                        />
                        {combinedRootSuggestions.length > 0 && (
                          <ul className="absolute z-10 w-full bg-white mt-1 border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                            {combinedRootSuggestions.map((suggestion, index) => (
                              <li
                                key={`combined-root-${suggestion.srno || index}`}
                                onClick={() => {
                                  setCombinedRootValue(suggestion.Root);
                                  setSkip(prev => [...prev, "combinerootname"])
                                  localStorage.setItem('selectedRootCode', suggestion.SrNo);
                                  setCombinedRootSuggestions([]);
                                }}
                                className="px-4 py-2 hover:bg-blue-50 cursor-pointer text-gray-700 transition-colors border-b border-gray-100 last:border-0"
                              >
                                {suggestion.Root}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>

                      <div className="relative">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Salesman Name</label>
                        <input
                          type="text"
                          value={combinedSalesmanValue}
                          onChange={handleChange(setCombinedSalesmanValue)}
                          placeholder="Enter Salesman Name"
                          autoComplete="off"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none transition-colors text-gray-700"
                        />
                        {combinedSalesmanSuggestions.length > 0 && (
                          <ul className="absolute z-10 w-full bg-white mt-1 border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                            {combinedSalesmanSuggestions.map((item) => (
                              <li
                                key={item.Code || item.code || item.salesmanCode || item.salesmancode || item.SalesmanCode || item.Salesmancode}
                                onClick={() => {
                                  handleSuggestionClick(item.Name || item.salesmanName || item.name || item.salesmanname, setCombinedSalesmanValue, "combinesalesman")
                                  setCombinedSalesmanSuggestions([]);
                                }}
                                className="px-4 py-2 hover:bg-blue-50 cursor-pointer text-gray-700 transition-colors border-b border-gray-100 last:border-0"
                              >
                                {item.Name || item.salesmanName || item.name || item.salesmanname}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  )}

                  {criteria === 'subagentwise' && (
                    <div className="relative">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Sub Agent Name</label>
                      <input
                        type="text"
                        value={subAgentValue}
                        onChange={handleChange(setSubAgentValue)}
                        placeholder="Enter Sub Agent Name"
                        autoComplete="off"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none transition-colors text-gray-700"
                      />
                      {subAgentSuggestions.length > 0 && (
                        <ul className="absolute z-10 w-full bg-white mt-1 border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {subAgentSuggestions.map((item) => (
                            <li
                              key={item.Code || item.code || item.salesmanCode || item.salesmancode || item.SalesmanCode || item.Salesmancode}
                              onClick={() => {
                                handleSuggestionClick(
                                  item.Name || item.salesmanName || item.name || item.salesmanname,
                                  setSubAgentValue,
                                  "subagentSuggestion"
                                );
                                localStorage.setItem('selectedSubAgentCode', item.Code || item.code || item.salesmanCode || item.salesmancode || item.SalesmanCode || item.Salesmancode);
                                setSubAgentSuggestions([]);
                              }}
                              className="px-4 py-2 hover:bg-blue-50 cursor-pointer text-gray-700 transition-colors border-b border-gray-100 last:border-0"
                            >
                              {item.Name || item.salesmanName || item.name || item.salesmanname}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-medium text-sm hover:bg-blue-700 focus:ring-4 focus:ring-blue-200 transition-all duration-200 shadow-sm hover:shadow-md"
                  >
                    Generate Report
                  </button>
                </div>
              </form>

              {error && (
                <div className="mt-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-r-lg">
                  <p className="font-medium">Error</p>
                  <p>{error}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </Box>

      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm">
          <div className="bg-white p-6 rounded-2xl shadow-2xl flex flex-col items-center">
            <Lottie animationData={loader} loop={true} autoplay={true} style={{ height: 120, width: 120 }} />
            <p className="mt-4 text-gray-600 font-medium">Processing...</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default Outstanding;
